import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Sparkles, Check } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import CheckoutDetailsModal from "./CheckoutDetailsModal";

export default function UnlockBanner({ profileId, stats }) {
  const [showDetails, setShowDetails] = useState(false);

  const startCheckout = () => {
    trackEvent("unlock_clicked", { profile_id: profileId, source: "banner" });
    setShowDetails(true);
  };

  const benefits = [
    "Full personalised synthesis (6 chapters)",
    "Shadow → Gift → Siddhi for your 4 prime gifts",
    "Daily-life & work guidance",
    "Downloadable PDF — yours to keep forever",
    "Lifetime access to your blueprint",
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7 }}
      className="relative my-10 rounded-3xl overflow-hidden"
      data-testid="unlock-banner"
    >
      {/* Decorative glow */}
      <div className="absolute inset-0 -z-0 bg-gradient-to-br from-[#D4AF37]/15 via-transparent to-[#D4AF37]/5" />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full bg-violet-500/10 blur-3xl pointer-events-none" />

      <div className="relative glass-card rounded-3xl p-8 md:p-10 border-[#D4AF37]/30">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left side: pitch */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              </span>
              <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]">
                the full reading
              </p>
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
              Your blueprint is <span className="italic gold-text">ready.</span>
              <br />
              Reveal what the stars wrote for you.
            </h3>
            {stats && (
              <p className="mt-5 font-sans text-sm text-slate-300">
                <span className="text-white">{stats.chapters || 6}</span> chapters
                <span className="mx-2 text-slate-600">·</span>
                <span className="text-white">{stats.gene_keys || 4}</span> gene keys
                <span className="mx-2 text-slate-600">·</span>
                <span className="text-white">{(stats.words || 0).toLocaleString()}</span> words personally woven for you
              </p>
            )}
            <ul className="mt-7 space-y-2.5">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 font-sans text-sm text-slate-200">
                  <span className="mt-0.5 w-4 h-4 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                    <Check className="w-2.5 h-2.5 text-[#D4AF37]" strokeWidth={3} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right side: pricing card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl border border-[#D4AF37]/40 bg-[#040508]/60 backdrop-blur-xl p-7 text-center">
              <span
                data-testid="founder-tag"
                className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-sans uppercase tracking-overline whitespace-nowrap"
              >
                Limited founder rate
              </span>
              <p className="font-sans text-[10px] uppercase tracking-overline text-slate-400 mt-2">
                One-time · lifetime access
              </p>
              <div className="mt-3 flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
                <span className="font-serif text-xl sm:text-2xl text-slate-500 line-through" data-testid="price-original">$88 AUD</span>
                <span className="font-serif text-5xl sm:text-6xl text-white whitespace-nowrap" data-testid="price-current">$44.44 AUD</span>
              </div>
              <button
                data-testid="unlock-cta"
                onClick={startCheckout}
                className="btn-gold rounded-full mt-7 w-full py-4 px-4 text-[11px] sm:text-xs flex items-center justify-center gap-2"
              >
                <Lock className="w-4 h-4 shrink-0" />
                <span className="sm:hidden">Unlock · $44.44 AUD</span>
                <span className="hidden sm:inline">Unlock full reading · $44.44 AUD</span>
              </button>
              <p className="mt-4 text-[11px] text-slate-400 font-sans flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> Secured by Stripe · Instant access
              </p>
            </div>
          </div>
        </div>
      </div>

      <CheckoutDetailsModal
        open={showDetails}
        onClose={() => setShowDetails(false)}
        profileId={profileId}
      />
    </motion.div>
  );
}

/**
 * Sticky bottom CTA that appears after scroll into the locked content.
 */
export function UnlockStickyCTA({ profileId, visible }) {
  const [showDetails, setShowDetails] = useState(false);
  if (!visible) return null;

  const go = () => {
    trackEvent("unlock_clicked", { profile_id: profileId, source: "sticky" });
    setShowDetails(true);
  };

  return (
    <div
      data-testid="unlock-sticky-cta"
      className="fixed bottom-4 inset-x-4 sm:bottom-6 sm:right-6 sm:left-auto z-40 max-w-md mx-auto"
    >
      <div className="rounded-full glass-card border-[#D4AF37]/30 px-4 py-2 flex items-center gap-3 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]">
        <span className="hidden sm:flex w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]">unlock</p>
          <p className="font-serif text-sm text-white truncate">$44.44 AUD · lifetime access + PDF</p>
        </div>
        <button
          data-testid="unlock-sticky-button"
          onClick={go}
          className="btn-gold rounded-full px-4 py-2 text-[10px] flex items-center gap-1.5 shrink-0"
        >
          <Lock className="w-3 h-3" />
          Unlock
        </button>
      </div>
      <CheckoutDetailsModal
        open={showDetails}
        onClose={() => setShowDetails(false)}
        profileId={profileId}
      />
    </div>
  );
}
